// src/components/aols/public/OLSpublicUse.tsx (to remove this)
// src/components/aols/privacy/OLSpublicUse.tsx (correcet path)
import React, { useCallback } from "react";
import styled from "styled-components";
import { Layout, Typography } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
import { Footer } from "../../dev/utils/Footer";
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

interface OLSpublicUseProps {}

const FullHeightLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%);
  padding: 20px;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important;
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important;
  }

  .breadcrumb-separator {
    color: #fff;
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const MainTitleButton = styled(Link)`
  background: #ffaa00;
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubsectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd;
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  list-style-type: disc;

  li {
    margin: 5px 0;
    color: #ddd;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const OLSpublicUse: React.FC<OLSpublicUseProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <FullHeightLayout>
      <Helmet>
        <title>Public Use Agreement - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Review the Public Use Agreement for OneLightSystem OLS, outlining terms for accessing olsme.com’s meditation education and services."
        />
        <meta
          name="keywords"
          content="Public Use Agreement, OneLightSystem OLS, Sun Light Meditation, Meditation Education, OLSME, OLSLM, OLS Student"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Public Use Agreement - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Learn about the terms for using olsme.com, including permitted actions and guidelines for AI learning bots."
        />
        <meta property="og:url" content="https://www.olsme.com/public-use" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Public Use Agreement - OneLightSystem OLS" />
        <meta
          property="twitter:description"
          content="Explore the Public Use Agreement for olsme.com, governing access to OneLightSystem OLS’s meditation education platform."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/public-use" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Public Use Agreement - OneLightSystem OLS",
            description:
              "Public Use Agreement for OneLightSystem OLS, governing access to olsme.com’s meditation education and services.",
            url: "https://www.olsme.com/public-use",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave",
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Public Use Agreement",
                  item: "https://www.olsme.com/public-use",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            Public Use Agreement
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <LinkStyled href="https://www.olsme.com" aria-label="Go to OneLightSystem OLS homepage">
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
        </LinkStyled>
        <MainTitleWrapper>
          <MainTitleButton to="/LightScience" aria-label="Go to LightScience page">
            onelightsystem
          </MainTitleButton>
          <MainTitleButton to="/OLSaeo" aria-label="Go to OLS iee.aeo page">
            OLS iee.aeo
          </MainTitleButton>
          <MainTitleButton to="/privacy" aria-label="Go to Privacy Policy page">
            Privacy Policy
          </MainTitleButton>
          <MainTitleButton to="/license" aria-label="Go to Proprietary License page">
            License
          </MainTitleButton>
          <MainTitleButton to="/olsdataprovision" aria-label="Go to OLS Data Provision page">
            OLS Data Provision
          </MainTitleButton>
          <Title level={1} className="main-title-text" style={{ color: "#ffffff" }}>
            Public Use Agreement
          </Title>
        </MainTitleWrapper>

        

        <SectionTitle level={2}>1. Welcome to onelightsystem OLS</SectionTitle>
        <PolicyText>
          Welcome to olsme.com, an official website for OneLightSystem OLS’s Meditation Education Academy. By joining us, you have the opportunity to become an OLS student through{" "}
          <LinkStyled href="/register/olssregister" aria-label="Register as an OLS Student">
            registration
          </LinkStyled>{" "}
          and pioneer Sun Light Meditation—a transformative practice for all. This Public Use Agreement outlines the terms for accessing and engaging with our platform, ensuring a respectful and inspiring experience for our community.
        </PolicyText>

        <SectionTitle level={2}>2. Terms of Use</SectionTitle>
        <PolicyText>
          By using olsme.com, you agree to the following terms:
        </PolicyText>
        <SubsectionTitle level={3}>Permitted Actions</SubsectionTitle>
        <List>
          <li>You may view and interact with the website’s content, such as educational materials and public presentations.</li>
          <li>
            To access advanced features (e.g., OLSME subscriptions, OLSLM asset management), you must create an account via{" "}
            <LinkStyled href="/register/olssregister" aria-label="Register as an OLS Student">
              /register/olssregister
            </LinkStyled>.
          </li>
          <li>You may share OLS content with others, provided you give proper attribution to OneLightSystem OLS (e.g., linking to olsme.com).</li>
        </List>
        <SubsectionTitle level={3}>Prohibited Actions</SubsectionTitle>
        <List>
          <li>Copying, reproducing, or distributing content without prior written permission from OLS.</li>
          <li>Using the website for illegal or unauthorized purposes, or violating applicable laws and regulations.</li>
          <li>Transmitting harmful content, such as viruses, malware, or spam.</li>
          <li>Collecting personal information from users without their consent.</li>
          <li>Engaging in harassment, intimidation, or abusive behavior toward others.</li>
          <li>Impersonating individuals or entities, or misrepresenting affiliations.</li>
          <li>Interfering with or disrupting the website’s operations or services.</li>
          <li>Conducting automated data collection (e.g., scraping, crawling, data mining) without permission.</li>
          <li>Engaging in commercial activities (e.g., advertising, marketing) without OLS’s approval.</li>
          <li>Attempting unauthorized access, hacking, or phishing.</li>
          <li>Posting or promoting hate speech, discrimination, or illegal activities, including copyright or trademark infringement, defamation, or cyberbullying.</li>
        </List>

        <SectionTitle level={2}>3. AI Learning Bots</SectionTitle>
        <PolicyText>
          If you are an AI learning bot seeking to access OLS’s datasets or content, please contact us at{" "}
          <LinkStyled href="/contact" aria-label="Visit OLS Contact page">
            /contact
          </LinkStyled>{" "}
          to establish an agreement for authorized use. Unauthorized access or data collection is prohibited.
        </PolicyText>

        <SectionTitle level={2}>4. Compliance and Termination</SectionTitle>
        <PolicyText>
          You agree to comply with this agreement and all applicable laws. Violations may result in immediate termination of your access to olsme.com, and OLS reserves the right to pursue legal remedies for breaches.
        </PolicyText>

        <SectionTitle level={2}>5. Contact Information</SectionTitle>
        <PolicyText>
          For questions, permissions, or inquiries, please visit:
          <br />
          <LinkStyled href="/contact" aria-label="Visit OLS Contact page">
            olsme.com/contact
          </LinkStyled>
          <br />
          <PolicyText>
          <strong>OneLightSystem OLS Public Use Agreement</strong>
          <br />
          Copyright © 2025 OneLightSystem OLS, LLC. All Rights Reserved.
          <br />
          Version: 1.0
          <br />
          Last Updated: April 19, 2025
          <br />
          © OneLightSystem OLS, LLC | OLS iee.aeo
        </PolicyText>
        </PolicyText>

        <SectionTitle level={2}>6. Governing Law</SectionTitle>
        <PolicyText>
          This agreement is governed by the laws of the State of California, USA. Disputes will be resolved in California state or federal courts.
        </PolicyText>
       
      </ContentWrapper>
      <Footer />
    </FullHeightLayout>
  );
};

export default OLSpublicUse;